import { useState, useEffect } from "react";
import { Range } from "react-range";
import { Body, BodyColor, BodyType, } from "..";
import './Slider.css';
import { useRecoilValue } from "recoil";
import { userCurrencyState } from "../../states";
import { calculateSmartStep, compressNumber, getSnappedSliderRange } from "../../helpers";
import { SliderThumb, SliderTrack } from ".";

interface SliderProps {
	min: number;
	max: number;
	rangeMin: number;
	rangeMax: number;
	sliderKey: string;
	onRangeChange: (min: number, max: number) => void;
}

export function Slider({ min, max, rangeMin, rangeMax, sliderKey, onRangeChange }: SliderProps) {
	const selCurrency = useRecoilValue(userCurrencyState);

	// Helper function to ensure valid numbers
	const ensureValidNumber = (value: number, fallback: number): number => {
		return isNaN(value) || !isFinite(value) ? fallback : value;
	};

	const getInitialValues = (): number[] => {
		const validMin = ensureValidNumber(min, 0);
		const validMax = ensureValidNumber(max, 100);
		const validRangeMin = ensureValidNumber(rangeMin, validMin);
		const validRangeMax = ensureValidNumber(rangeMax, validMax);

		return [
			Math.max(validMin, validRangeMin),
			Math.min(validMax, validRangeMax)
		];
	};

	const [values, setValues] = useState<number[]>(getInitialValues());

	useEffect(() => {
		// Validate all input values
		const validMin = ensureValidNumber(min, 0);
		const validMax = ensureValidNumber(max, 100);
		const validRangeMin = ensureValidNumber(rangeMin, validMin);
		const validRangeMax = ensureValidNumber(rangeMax, validMax);

		// Don't allow invalid sync (e.g., child's max 7 updating parent's 39)
		const safeMin = Math.max(validMin, Math.min(validRangeMin, validRangeMax));
		const safeMax = Math.min(validMax, Math.max(validRangeMin, validRangeMax));

		if (safeMin !== values[0] || safeMax !== values[1]) {
			setValues([safeMin, safeMax]);
		}
	}, [sliderKey, rangeMin, rangeMax, min, max]);

	const handleInput = (values: number[]) => {
		const [newMin, newMax] = values;
		const snappedRange = getSnappedSliderRange(min, max, newMin, newMax);

		setValues([snappedRange.min, snappedRange.max]);
		onRangeChange(snappedRange.min, snappedRange.max);
	};

	const step = calculateSmartStep(min, max);

	// Additional validation: ensure values array doesn't contain NaN
	const hasInvalidValues = values.some(v => isNaN(v) || !isFinite(v));
	const isInvalidRange = isNaN(min) || isNaN(max) || !isFinite(min) || !isFinite(max);

	if (hasInvalidValues || isInvalidRange || (min >= max) || (values[0] > Math.max(min, Math.min(rangeMin, rangeMax))) || (values[1] < Math.min(max, Math.max(rangeMin, rangeMax)))|| values[1]>max ||values[0]<min) return <>
	{ <div style={{ position: 'relative', width: '100%', margin: '60px auto' }}>
      {/* Horizontal Line */}
      <div style={{
        height: '4px',
        backgroundColor: '#ddd',
        width: '100%',
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)'
      }} />

      {/* Tooltip Box */}
      <div style={{
        position: 'absolute',
        left: '51.8%',
        bottom: '15px',
        transform: 'translateX(-100%)',
        backgroundColor: '#555',
        color: 'white',
        padding: '6px 10px',
        borderRadius: '4px',
        fontSize: '14px',
        whiteSpace: 'nowrap'
      }}>
        {values[0]}
      </div>

      {/* Slider Thumb */}
      <div style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#eee',
        border: '2px solid #aaa'
      }} />
    </div>}</>;

	return (
		<div className="position-relative w-100 padding-5" key={sliderKey}>
			<Body
				color={BodyColor.secondary}
				type={BodyType.p3}
				classname="position-absolute"
				style={{ bottom: "0", left: "2%" }}
			>
				{selCurrency == "USD" ? compressNumber(min, selCurrency, false, true, true, sliderKey === "core_transaction_value") : compressNumber(min, selCurrency, false, true, true, sliderKey === "core_transaction_value")}
			</Body>
			<Body
				color={BodyColor.secondary}
				type={BodyType.p3}
				classname="position-absolute"
				style={{ bottom: "0", right: "2%" }}
			>
				{selCurrency == "USD" ? compressNumber(max, selCurrency, false, true, true, sliderKey === "core_transaction_value") : compressNumber(max, selCurrency, false, true, true, sliderKey === "core_transaction_value")}
			</Body>

			<Range
				key={sliderKey}
				label="Select price range"
				labelledBy="two-thumbs-slider-label"
				step={step}
				min={min}
				max={max}
				values={values}
				onChange={handleInput}
				// onFinalChange={onFinalChange}
				renderTrack={({ props, children }) => (
					<SliderTrack
						props={props}
						children={children}
						values={values}
						min={min}
						max={max}
					/>
				)}
				renderThumb={({ props, index, isDragged }) => (
					<SliderThumb
						props={props}
						index={index}
						values={values}
						selCurrency={selCurrency}
						sliderKey={sliderKey}
					/>
				)}
			/>
		</div>
	);
}